/**
 * Pure helpers that turn a backend image-model descriptor into the options the
 * UI may render.
 *
 * The backend catalog is the source of truth for quality levels and sizes, so
 * everything arrives as plain strings. These helpers narrow them to the values
 * this build knows about — an unknown quality is dropped rather than rendered
 * as an option the user can pick but the backend would reject.
 *
 * No React, no store, no network: safe to unit-test and to call during render.
 */

import type { StudioImageModel } from "@/features/studio-ai/types/studio-ai.types"

import { ImageQuality } from "@/features/studio-ai/types/studio-ai.types"

/** Every quality this build can render, in the order it should be offered. */
export const IMAGE_QUALITY_ORDER: readonly ImageQuality[] = [
  ImageQuality.Low,
  ImageQuality.Medium,
  ImageQuality.High,
  ImageQuality.XHigh,
  ImageQuality.Max,
  ImageQuality.Auto,
]

/**
 * What the pre-2.5 models offer. Used when no model list has loaded yet (or the
 * backend predates the picker) so the panel keeps working exactly as before.
 */
export const FALLBACK_IMAGE_QUALITIES: readonly ImageQuality[] = [
  ImageQuality.Low,
  ImageQuality.Medium,
  ImageQuality.High,
]

/** `studioAi.imageSettings.*` label key per quality (compact Select labels). */
export const IMAGE_QUALITY_LABEL_KEYS: Record<ImageQuality, string> = {
  [ImageQuality.Low]: "lowQuality",
  [ImageQuality.Medium]: "mediumQuality",
  [ImageQuality.High]: "highQuality",
  [ImageQuality.XHigh]: "xhighQuality",
  [ImageQuality.Max]: "maxQuality",
  [ImageQuality.Auto]: "autoQuality",
}

/** `studioAi.image.quality.*` label/description keys per quality (wizard cards). */
export const IMAGE_QUALITY_WIZARD_KEYS: Record<
  ImageQuality,
  { labelKey: string; descKey: string }
> = {
  [ImageQuality.Low]: { labelKey: "low", descKey: "lowDesc" },
  [ImageQuality.Medium]: { labelKey: "medium", descKey: "mediumDesc" },
  [ImageQuality.High]: { labelKey: "high", descKey: "highDesc" },
  [ImageQuality.XHigh]: { labelKey: "xhigh", descKey: "xhighDesc" },
  [ImageQuality.Max]: { labelKey: "max", descKey: "maxDesc" },
  [ImageQuality.Auto]: { labelKey: "auto", descKey: "autoDesc" },
}

/** `studioAi.imageSettings.*` key for a model's tier hint. */
export const IMAGE_TIER_LABEL_KEYS: Record<string, string> = {
  fast: "modelTierFast",
  quality: "modelTierQuality",
}

export interface ImageModelCapabilities {
  /** Quality levels to offer, backend order, unknown values dropped. */
  qualities: ImageQuality[]
  /** The level to fall back to when the current one isn't offered. */
  defaultQuality: ImageQuality
  /**
   * Sizes the model accepts; empty when the model list hasn't loaded. Not yet
   * consumed by the size controls: every family in the catalog offers the same
   * three sizes, and custom `WIDTHxHEIGHT` sizes are the follow-up in plan §10.
   */
  sizes: string[]
  /** False only when the backend says so — absence means "assume supported". */
  supportsTransparent: boolean
}

function isKnownQuality(value: string): value is ImageQuality {
  return (IMAGE_QUALITY_ORDER as readonly string[]).includes(value)
}

/**
 * Capabilities of `model`, or the pre-2.5 defaults when it is null/undefined
 * (settings still loading, or a backend without the model registry).
 */
export function getImageModelCapabilities(model?: StudioImageModel | null): ImageModelCapabilities {
  const offered = (model?.qualities ?? []).filter(isKnownQuality)
  const qualities = offered.length > 0 ? [...new Set(offered)] : [...FALLBACK_IMAGE_QUALITIES]

  const declaredDefault = model?.default_quality
  let defaultQuality: ImageQuality
  if (declaredDefault && isKnownQuality(declaredDefault) && qualities.includes(declaredDefault)) {
    defaultQuality = declaredDefault
  } else if (qualities.includes(ImageQuality.Medium)) {
    defaultQuality = ImageQuality.Medium
  } else {
    defaultQuality = qualities[0]
  }

  return {
    qualities,
    defaultQuality,
    sizes: (model?.sizes ?? []).filter((size) => size.length > 0),
    supportsTransparent: model ? model.supports_transparent !== false : true,
  }
}

/** The models a user may actually pick: everything the endpoint listed, minus
 *  anything explicitly marked disabled. */
export function selectableModels(models?: StudioImageModel[] | null): StudioImageModel[] {
  return (models ?? []).filter((model) => model.enabled !== false)
}

/**
 * Resolve the model a request should use.
 *
 * A persisted `modelId` that no longer names a listed model (deployment removed
 * or disabled by an admin) falls back to the Studio default, then to the first
 * listed model. Returns undefined when nothing is listed at all.
 */
export function resolveSelectedImageModel(
  models: StudioImageModel[],
  modelId?: string,
  defaultModelId?: string
): StudioImageModel | undefined {
  if (models.length === 0) return undefined
  return (
    (modelId ? models.find((model) => model.id === modelId) : undefined) ??
    (defaultModelId ? models.find((model) => model.id === defaultModelId) : undefined) ??
    models[0]
  )
}
